import { useLayoutEffect, useMemo, useRef } from 'react';

import { useInfiniteQuery } from '@tanstack/react-query';

import { AGGREGATION_FIELDS, ASSET_TYPES } from '@/shared/api/openmetadata';
import { useDebounce } from '@/shared/hooks/useDebounce';
import { EXPERIMENT_ASSET_TYPES_FULL } from '@/shared/lib/constants/experimentAssets';
import { discoveryKeys } from '@/shared/lib/query/queryKeys';
import { getRetryDelay, shouldRetryRequest } from '@/shared/lib/query/retryConfig';

import { mapAssetSearchResultToSearchResult } from '@/entities/asset-search';
import { isExcludedFromDiscoveryAssetTypeFilter } from '@/entities/asset-search/domain/constants';
import type { FilterState } from '@/entities/discovery/domain/types';
import { mapOpenMetadataToSearchResult } from '@/entities/search';
import { useSearchStore } from '@/entities/search';

import { searchIntegrationWithPagination } from '@/features/dataSearch/api/integrationSearchApi';
import { searchOpenMetadata } from '@/features/dataSearch/api/openmetadataSearchApi';

import { mergeSearchResults, SearchCache } from './_internal/cacheManager';

const PAGE_SIZE = 15;
const DB_FETCH_SIZE = 15;
const OMD_FETCH_SIZE = 15;

const certificationFilterExcludesExperimentIntegration = (certification: string[] | undefined): boolean => {
  if (!certification || certification.length === 0) return false;
  return !certification.some((c) => c.toLowerCase().includes('gold'));
};

const certificationFilterIncludesGold = (certification: string[] | undefined): boolean =>
  certification?.some((c) => c.toLowerCase().includes('gold')) ?? false;

const STANDALONE_GOLD_PROXY_ASSET_TYPES = [ASSET_TYPES.FEATURE_SET, ASSET_TYPES.DATASET] as const;

/**
 * 실험 데이터 조회 여부 판단
 *
 * @description
 * 현재 필터 상태를 분석하여 실험 관련 자산(run, feature_set, dataset, artifact)을
 * 조회해야 하는지 결정합니다.
 *
 * 조회 제외 조건:
 * - 실험 외 필터가 활성화된 경우 (dataSource, domain, owner, tier, tags)
 * - certification 필터에 gold가 없는 경우
 * - assetType 필터에 실험 관련 타입이 없는 경우
 *
 * 특수 처리:
 * - certification에 gold가 포함되면 feature_set/dataset을 포함
 *
 * @param filters - 현재 필터 상태
 * @returns 실험 데이터 조회 여부
 */
const shouldFetchExperimentData = (filters: FilterState | null | undefined): boolean => {
  if (!filters) return true;

  const { dataSource, domain, owner, tier, tags, certification, assetType } = filters;

  const hasNonAssetTypeFilters =
    (dataSource && dataSource.length > 0) ||
    (domain && domain.length > 0) ||
    (owner && owner.length > 0) ||
    (tier && tier.length > 0) ||
    (tags && tags.length > 0) ||
    certificationFilterExcludesExperimentIntegration(certification);

  if (hasNonAssetTypeFilters) {
    return false;
  }

  if (certificationFilterIncludesGold(certification)) {
    if (!assetType || assetType.length === 0) {
      return true;
    }
    return assetType.some((t) => t === ASSET_TYPES.FEATURE_SET || t === ASSET_TYPES.DATASET);
  }

  if (!assetType || assetType.length === 0) {
    return true;
  }

  return assetType.some((type) =>
    EXPERIMENT_ASSET_TYPES_FULL.includes(type as (typeof EXPERIMENT_ASSET_TYPES_FULL)[number])
  );
};

const buildDbParams = (
  query: string,
  filters: FilterState | null | undefined,
  sortBy: string,
  cursor: string | null
) => {
  const isNameSort = sortBy === 'name';
  const isOldestFirst = sortBy === 'lastUpdatedAsc';

  const params: Record<string, unknown> = {
    query: query || undefined,
    size: DB_FETCH_SIZE,
    cursor: cursor || undefined,
    sortBy: isNameSort ? 'name' : 'created_at',
    sortOrder: isNameSort || isOldestFirst ? 'asc' : 'desc',
  };

  if (filters?.quickFilter) {
    const { projectId, experimentId, runId } = filters.quickFilter;
    if (projectId) params.projectId = projectId;
    if (experimentId) params.experimentId = experimentId;
    if (runId) params.runId = runId;
  }

  const discoveryAssetTypes = filters?.assetType?.filter((t) => !isExcludedFromDiscoveryAssetTypeFilter(t)) ?? [];

  if (certificationFilterIncludesGold(filters?.certification)) {
    if (discoveryAssetTypes.length > 0) {
      const intersect = discoveryAssetTypes.filter((t) => STANDALONE_GOLD_PROXY_ASSET_TYPES.some((type) => type === t));
      params.assetTypes = intersect.length > 0 ? intersect : [...STANDALONE_GOLD_PROXY_ASSET_TYPES];
    } else {
      params.assetTypes = [...STANDALONE_GOLD_PROXY_ASSET_TYPES];
    }
    return params;
  }

  if (discoveryAssetTypes.length > 0) {
    const expandedAssetTypes = discoveryAssetTypes.flatMap((type) => {
      if (type === ASSET_TYPES.FEATURE_SET) {
        return [ASSET_TYPES.FEATURE_SET, 'run_feature_set'];
      }
      if (type === ASSET_TYPES.DATASET) {
        return [ASSET_TYPES.DATASET, 'run_dataset'];
      }
      if (type === ASSET_TYPES.ARTIFACT) {
        return ['run_artifact'];
      }
      return [type];
    });
    params.assetTypes = [...new Set(expandedAssetTypes)];
  }

  return params;
};

export const useSearch = () => {
  const { query, sortBy, filters, setAggregations, initialAggregations } = useSearchStore();

  const debouncedQuery = useDebounce(query, 300);

  const mergedCacheRef = useRef(new SearchCache());
  /** OpenMetadata `from` — Elasticsearch 오프셋(건너뛸 문서 수). 페이지 번호가 아님. */
  const omdFromOffsetRef = useRef(0);
  const dbCursorRef = useRef<string | null>(null);
  const dbExhaustedRef = useRef(false);
  const omdExhaustedRef = useRef(false);
  const dbTotalCountRef = useRef(0);
  const omdTotalCountRef = useRef(0);
  const searchQueryKey = useMemo(
    () => discoveryKeys.search(debouncedQuery, sortBy, filters),
    [debouncedQuery, sortBy, filters]
  );

  useLayoutEffect(() => {
    mergedCacheRef.current.clear();
    omdFromOffsetRef.current = 0;
    dbCursorRef.current = null;
    dbExhaustedRef.current = false;
    omdExhaustedRef.current = false;
    dbTotalCountRef.current = 0;
    omdTotalCountRef.current = 0;
  }, [searchQueryKey]);

  const infiniteQuery = useInfiniteQuery({
    queryKey: searchQueryKey,
    queryFn: async ({ pageParam }) => {
      const { filters: currentFilters, sortBy: currentSortBy } = useSearchStore.getState();

      const mergedCache = mergedCacheRef.current;
      const isFirstPage = pageParam === 0;

      if (isFirstPage) {
        mergedCache.clear();
        omdFromOffsetRef.current = 0;
        dbCursorRef.current = null;
        dbExhaustedRef.current = false;
        omdExhaustedRef.current = false;
        dbTotalCountRef.current = 0;
        omdTotalCountRef.current = 0;
      }

      const defaultFilters: FilterState = {
        dataSource: [],
        assetType: [],
        domain: [],
        owner: [],
        tier: [],
        tags: [],
        certification: [],
      };

      const availableServiceTypes =
        initialAggregations?.[AGGREGATION_FIELDS.SERVICE_TYPE]?.buckets.map((bucket) => bucket.key) || [];

      const shouldFetchDb = shouldFetchExperimentData(currentFilters);

      const shouldFetchOmd = !omdExhaustedRef.current;

      const [omdResponse, dbResponse] = await Promise.all([
        shouldFetchOmd
          ? searchOpenMetadata(
              debouncedQuery,
              currentFilters || defaultFilters,
              omdFromOffsetRef.current,
              OMD_FETCH_SIZE,
              currentSortBy,
              availableServiceTypes
            )
          : Promise.resolve(null),
        !shouldFetchDb || dbExhaustedRef.current
          ? Promise.resolve(null)
          : searchIntegrationWithPagination(
              buildDbParams(debouncedQuery, currentFilters, currentSortBy, dbCursorRef.current)
            ),
      ]);

      if (isFirstPage && omdResponse?.aggregations) {
        setAggregations(omdResponse.aggregations);
      }

      const omdItems = omdResponse ? omdResponse.hits.hits.map((hit) => mapOpenMetadataToSearchResult(hit)) : [];
      const dbItems = dbResponse ? dbResponse.results.map(mapAssetSearchResultToSearchResult) : [];

      if (isFirstPage) {
        omdTotalCountRef.current = omdResponse?.hits.total.value || 0;
        dbTotalCountRef.current = dbResponse?.totalCount || 0;
      }

      if (shouldFetchOmd && omdItems.length === 0) {
        omdExhaustedRef.current = true;
      }

      if (shouldFetchOmd) {
        omdFromOffsetRef.current += omdItems.length;
      }

      if (!dbResponse?.hasMore) {
        dbExhaustedRef.current = true;
      } else {
        dbCursorRef.current = dbResponse.nextCursor;
      }

      const allNewItems = [...omdItems, ...dbItems];
      const existingCache = mergedCache.getAll();

      if (allNewItems.length === 0 && existingCache.length === 0) {
        return {
          items: [],
          totalCount: omdTotalCountRef.current + dbTotalCountRef.current,
          nextPageParam: undefined,
          aggregations: omdResponse?.aggregations,
        };
      }

      if (allNewItems.length > 0) {
        const mergedAndSorted = mergeSearchResults(
          allNewItems,
          existingCache,
          currentSortBy === 'name' ? 'name' : currentSortBy === 'lastUpdatedAsc' ? 'lastUpdatedAsc' : 'lastUpdated'
        );
        mergedCache.clear();
        mergedCache.add(mergedAndSorted);
      }

      const pageItems = mergedCache.take(PAGE_SIZE);

      const hasMore = !dbExhaustedRef.current || !omdExhaustedRef.current || mergedCache.size() > 0;

      return {
        items: pageItems,
        totalCount: omdTotalCountRef.current + dbTotalCountRef.current,
        nextPageParam: hasMore ? (pageParam || 0) + 1 : undefined,
        aggregations: omdResponse?.aggregations,
      };
    },
    getNextPageParam: (lastPage) => lastPage.nextPageParam,
    initialPageParam: 0,
    enabled: true,
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: shouldRetryRequest,
    retryDelay: getRetryDelay,
  });

  return { ...infiniteQuery, searchQueryKey };
};
